import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun MaterialIcon(name: String, attrs: String = "") {
    Span(attrs = {
        classes("material-icons")
        if (attrs.isNotBlank()) {
            attrs.split(" ").filter { it.isNotBlank() }.forEach { classes(it) }
        }
    }) {
        Text(name)
    }
}

@Composable
fun SkeletonLoader(classes: List<String> = listOf("h-24", "rounded-lg")) {
    Div(attrs = {
        classes(*classes.toTypedArray())
        classes("relative", "overflow-hidden", "bg-white/5")
    }) {
        // Shimmer effect - the "presijavanje"
        Div(attrs = {
            classes(
                "absolute", "inset-0",
                "-translate-x-full",
                "animate-shimmer",
                "bg-gradient-to-r",
                "from-transparent",
                "via-white/10",
                "to-transparent"
            )
        })
    }
}


@Composable
fun RecipeSkeletonItem() {
    Div(attrs = { classes("bg-nano-surface", "rounded-[32px]", "overflow-hidden", "border", "border-white/5", "animate-pulse") }) {
        // Image placeholder with shimmer
        Div(attrs = { classes("h-64", "bg-white/5", "relative", "overflow-hidden") }) {
            Div(attrs = {
                classes(
                    "absolute", "inset-0",
                    "-translate-x-full",
                    "animate-shimmer",
                    "bg-gradient-to-r",
                    "from-transparent",
                    "via-white/10",
                    "to-transparent"
                )
            })
        }
        Div(attrs = { classes("p-6", "space-y-4") }) {
            SkeletonLoader(listOf("w-20", "h-3", "rounded-full"))
            SkeletonLoader(listOf("w-3/4", "h-6", "rounded-xl"))
            Div(attrs = { classes("flex", "gap-4") }) {
                SkeletonLoader(listOf("w-16", "h-4", "rounded-lg"))
                SkeletonLoader(listOf("w-16", "h-4", "rounded-lg"))
            }
        }
    }
}

@Composable
fun RecipeDetailsSkeleton() {
    Div(attrs = { classes("max-w-5xl", "mx-auto", "px-4", "py-12") }) {
        // Back & Like Header Placeholder
        Div(attrs = { classes("flex", "justify-between", "items-center", "mb-8") }) {
            SkeletonLoader(listOf("w-32", "h-12", "rounded-xl"))
            SkeletonLoader(listOf("w-12", "h-12", "rounded-xl"))
        }

        // Hero Image Placeholder
        SkeletonLoader(listOf("h-[400px]", "w-full", "rounded-3xl", "mb-12"))

        // Info Grid Placeholder
        Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-3", "gap-8", "mb-12") }) {
            repeat(3) { SkeletonLoader(listOf("h-32", "rounded-3xl")) }
        }

        // Description Placeholder
        Div(attrs = { classes("bg-nano-surface", "p-8", "rounded-3xl", "border", "border-white/5", "mb-12") }) {
            SkeletonLoader(listOf("h-6", "w-48", "mb-6"))
            SkeletonLoader(listOf("h-4", "w-full", "mb-3"))
            SkeletonLoader(listOf("h-4", "w-full", "mb-3"))
            SkeletonLoader(listOf("h-4", "w-3/4"))
        }

        // Two Columns Placeholder
        Div(attrs = { classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-12") }) {
            repeat(2) {
                Div(attrs = { classes("bg-nano-surface/50", "p-8", "rounded-3xl", "border", "border-white/5") }) {
                    SkeletonLoader(listOf("h-8", "w-1/2", "mb-8"))
                    Div(attrs = { classes("space-y-6") }) {
                        repeat(5) {
                            Div(attrs = { classes("flex", "gap-4") }) {
                                SkeletonLoader(listOf("w-10", "h-10", "rounded-xl", "flex-shrink-0"))
                                SkeletonLoader(listOf("h-10", "flex-1", "rounded-xl"))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun RecipeSkeletonGrid(count: Int = 6) {
    Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "lg:grid-cols-3", "gap-8") }) {
        repeat(count) {
            RecipeSkeletonItem()
        }
    }
}

@Composable
fun SearchSkeletonGrid() {
    Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "lg:grid-cols-3", "gap-8") }) {
        repeat(9) {
            RecipeSkeletonItem()
        }
    }
}

@Composable
fun CategorySkeletonItem() {
    SkeletonLoader(listOf("h-14", "w-32", "rounded-2xl"))
}

@Composable
fun LargeCategorySkeletonItem() {
    Div(attrs = {
        classes("bg-nano-surface", "p-10", "rounded-[32px]", "border", "border-white/5")
    }) {
        SkeletonLoader(listOf("h-8", "w-full", "rounded-xl"))
    }
}

@Composable
fun CategorySkeletonRow(count: Int = 6) {
    Div(attrs = { classes("flex", "gap-4", "overflow-x-auto", "no-scrollbar", "pb-4") }) {
        repeat(count) {
            CategorySkeletonItem()
        }
    }
}

@Composable
fun DetailCard(icon: String, label: String, value: String, textColor: String) {
    Div(attrs = {
        classes(
            "bg-nano-surface",
            "p-6",
            "rounded-3xl",
            "border",
            "border-white/5",
            "flex",
            "flex-col",
            "items-center",
            "text-center"
        )
    }) {
        MaterialIcon(icon, "text-gray-500 mb-2")
        P(attrs = {
            classes(
                "text-xs",
                "font-black",
                "text-gray-500",
                "uppercase",
                "tracking-widest",
                "mb-2"
            )
        }) { Text(label) }
        P(attrs = { classes("text-2xl", "font-bold", textColor) }) { Text(value) }
    }
}

fun getFullImageUrl(url: String): String {
    if (url.startsWith("http")) return url
    if (url.isEmpty()) return "https://via.placeholder.com/400x300?text=Nema+Slike"

    // Pretpostavljamo da su relativne putanje sa servera
    val baseUrl = "https://recipe.car4hire.ba"
    return if (url.startsWith("/")) "$baseUrl$url" else "$baseUrl/$url"
}